import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import type { AccessModel } from '@jwp/ott-common/types/config';
import type { Playlist, PlaylistItem } from '@jwp/ott-common/types/playlist';
import { mediaURL } from '@jwp/ott-common/src/utils/urlFormatting';
import { Breakpoint, type Breakpoints } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import BackwardArrow from '@jwp/ott-theme/assets/icons/arrow_backward.svg';
import { useUIControls } from '@jwp/ott-hooks-react/src/useUIControls';

import CardGrid from '../CardGrid/CardGrid';
import IconButton from '../IconButton/IconButton';
import { handleBackNavigate } from '../../utils/handleBackNavigate';

import styles from './SeeMoreItems.module.scss';
import { useTheme } from '../../../../../ThemeContext';

type Props = {
  playlist: Playlist;
  accessModel: AccessModel;
  hasSubscription: boolean;
  onCardHover?: (item: PlaylistItem) => void;
  customer: boolean;
  title?: string;
};

const cols: Breakpoints = {
  [Breakpoint.xs]: 2,
  [Breakpoint.sm]: 3,
  [Breakpoint.md]: 5,
  [Breakpoint.lg]: 5,
  [Breakpoint.xl]: 5,
};

const SeeMoreItems = ({ playlist, accessModel, hasSubscription, onCardHover, customer, title }: Props): JSX.Element => {
  const { t } = useTranslation('user');
  const navigate = useNavigate();
  const location = useLocation();
  const { singleClickPlayEnabled } = useUIControls();

  const { universalStyles } = useTheme();

  const handleBackClick = () => {
    handleBackNavigate(navigate);
  };
  const getURL = (playlistItem: PlaylistItem) => {
    const isSeriesContent = playlistItem.contentType == 'series';
    return mediaURL({
      id: playlistItem.mediaid,
      title: playlistItem.title,
      playlistId: playlistItem.feedid,
      play: !isSeriesContent && singleClickPlayEnabled,
      contentType: playlistItem.contentType,
      returnPath: location.pathname + location.search,
    });
  };

  return (
    <div>
      <div className={styles.header}>
        <div className={styles.headerTitlePart}>
          <IconButton className={styles.iconButton} aria-controls="sidebar" aria-haspopup="true" onClick={handleBackClick}>
            <img src={BackwardArrow} />
          </IconButton>
          <h1>{title || playlist?.title}</h1>
        </div>
      </div>
      {playlist?.playlist?.length > 0 ? (
        <CardGrid
          getUrl={getURL}
          playlist={playlist}
          onCardHover={onCardHover}
          cols={cols}
          isLoading={false}
          accessModel={accessModel}
          isLoggedIn={!!customer}
          hasSubscription={hasSubscription}
          headingLevel={2}
        />
      ) : (
        <p className={styles.emptyFavourites} style={universalStyles.primaryTextColor}>
          {t('playlist.no_item')}
        </p>
      )}
    </div>
  );
};

export default SeeMoreItems;
